/**
 * The Rapier physics world.
 *
 * Fixed timestep with capped catch-up substeps: a variable step makes collision response
 * frame-rate dependent, which on a phone that drops frames means the player passes
 * through walls they would have been stopped by on a desktop. Capping the substeps stops
 * a single long hitch — a GC pause, a backgrounded tab — from cascading into hundreds of
 * catch-up steps in one frame, which manifests as the world exploding on resume.
 *
 * The single-threaded Rapier build is used deliberately: the threaded one requires
 * SharedArrayBuffer and therefore COOP/COEP headers, which would constrain hosting for no
 * benefit at this scale.
 */

import { Physics } from '@react-three/rapier';
import type { ReactNode } from 'react';
import { GRAVITY, MAX_PHYSICS_SUBSTEPS, PHYSICS_TIMESTEP } from './constants';

type Props = {
  children: ReactNode;
  /** Physics stops while the game is paused or the device is in portrait. */
  paused?: boolean;
  /** Renders collider outlines. Never enable in production — it costs draw calls. */
  debug?: boolean;
};

export function PhysicsWorld({ children, paused = false, debug = false }: Props) {
  return (
    <Physics
      gravity={[0, GRAVITY, 0]}
      timeStep={PHYSICS_TIMESTEP}
      maxCcdSubsteps={MAX_PHYSICS_SUBSTEPS}
      paused={paused}
      debug={debug}
    >
      {children}
    </Physics>
  );
}
